import React, { useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import type SignatureCanvasType from 'react-signature-canvas';
import type { SignatureField } from '../../api/bookingApi';
import { getLocalToday } from '../../utils/date';

// --- Agreement signature field renderer ---
// Mounted by the v2 agreement signature step (V2AgreementSigStep); the V1 package
// renderer it was extracted from was retired with operaitor/backend#1604 slice 2.
// The 'v2' variant drops autoFocus (a focused date input pops the iOS date wheel
// mid step-transition; a focused text input throws the keyboard up during the
// slide), sizes the canvas for fingers, and makes Clear a real button. The
// default variant is byte-identical to the legacy rendering for v1/signing.

export function AgreementSigFieldRenderer({ field, formId, index, value, onChange, sigRefs, dateDefaultsToToday = true, variant = 'default', inputId }: {
  field: SignatureField;
  formId: string;
  index: number;
  value: string | undefined;
  onChange: (v: string) => void;
  sigRefs: React.MutableRefObject<Map<string, SignatureCanvasType | null>>;
  /** When false, date sig-fields render empty instead of auto-filling today. Defaults to true (legacy behavior). */
  dateDefaultsToToday?: boolean;
  variant?: 'default' | 'v2';
  /** id for the text/initials/date input, so an external <label htmlFor> can name it. */
  inputId?: string;
}) {
  const v2 = variant === 'v2';
  switch (field.type) {
    case 'signature':
      return (
        <div className="space-y-4">
          <div className={`border-2 border-gray-100 rounded-2xl overflow-hidden bg-white hover:border-primary/30 transition-colors ${v2 ? 'relative' : ''}`}>
            <SignatureCanvas
              ref={(el) => { sigRefs.current.set(`${formId}:${index}`, el); }}
              penColor="black"
              clearOnResize={false}
              canvasProps={{ className: v2 ? 'w-full h-40 sm:h-44 md:h-48' : 'w-full h-32 sm:h-40 md:h-48', style: { touchAction: 'none' } }}
            />
            {v2 && (
              <div aria-hidden className="pointer-events-none absolute inset-x-5 bottom-5 border-b border-dashed border-gray-300" />
            )}
          </div>
          {v2 ? (
            <button
              type="button"
              className="min-h-[44px] px-4 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
              onClick={() => sigRefs.current.get(`${formId}:${index}`)?.clear()}
            >
              Clear Signature
            </button>
          ) : (
            <button type="button" className="text-sm font-bold text-gray-500 hover:text-gray-600 uppercase tracking-widest" onClick={() => sigRefs.current.get(`${formId}:${index}`)?.clear()}>
              Clear Signature
            </button>
          )}
        </div>
      );
    case 'date':
      return <DateFieldRenderer value={value} onChange={onChange} defaultToToday={dateDefaultsToToday} autoFocus={!v2} inputId={inputId} />;

    case 'initials':
      return (
        <input
          id={inputId}
          autoFocus={!v2}
          enterKeyHint={v2 ? 'done' : undefined}
          type="text"
          maxLength={4}
          className="w-40 bg-transparent border-b-2 border-gray-100 focus:border-primary text-3xl sm:text-4xl py-2 outline-none transition-colors text-center uppercase tracking-widest"
          placeholder="JDS"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
        />
      );
    case 'text':
      return (
        <input
          id={inputId}
          autoFocus={!v2}
          enterKeyHint={v2 ? 'done' : undefined}
          className="w-full bg-transparent border-b-2 border-gray-100 focus:border-primary text-xl sm:text-2xl py-2 outline-none transition-colors placeholder:text-gray-200"
          placeholder="Type here..."
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    default:
      return null;
  }
}

export function DateFieldRenderer({ value, onChange, defaultToToday = true, autoFocus = true, inputId }: {
  value: string | undefined;
  onChange: (v: string) => void;
  defaultToToday?: boolean;
  autoFocus?: boolean;
  inputId?: string;
}) {
  const today = getLocalToday();

  useEffect(() => {
    if (defaultToToday && !value) onChange(today);
  }, [defaultToToday, value, today, onChange]);

  return (
    <input
      id={inputId}
      autoFocus={autoFocus}
      type="date"
      className="w-full bg-transparent border-b-2 border-gray-100 focus:border-primary text-xl sm:text-2xl py-2 outline-none transition-colors"
      value={defaultToToday ? value || today : value ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
